// SVGIcon.tsx
import clsx from 'clsx';

export interface SVGIconProps {
  color?: 'gray700' | 'gray400' | 'orange800' | 'white' | 'orange400';
  size?: number;
  weight?: 'normal' | 'bold';
  rotate?: number;
  className?: string;
  onClick?: () => void;
}

const getStrokeColor = (color: string) => {
  switch (color) {
    case 'gray700':
      return '#4C5057';
    case 'gray400':
      return '#C7CBD1';
    case 'orange800':
      return '#FF600C';
    case 'orange400':
      return '#FFBC99';
    case 'white':
      return '#FFFFFF';
    default:
      return '#4C5057';
  }
};

const SVGIconBase = ({
  color = 'gray700',
  size = 16,
  rotate = 0,
  children,
  onClick,
  className,
}: SVGIconProps & { children: React.ReactNode }) => {
  return (
    <svg
      width={size}
      height={size}
      fill="none"
      onClick={onClick}
      viewBox="0 0 16 16"
      stroke={getStrokeColor(color)}
      className={clsx(className, {
        'rotate-90': rotate === 90,
        'rotate-180': rotate === 180,
        '-rotate-90': rotate === 270,
      })}
    >
      {children}
    </svg>
  );
};

const LinkIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  return (
    <SVGIconBase {...props}>
      <path
        d="M6.0901 3.54237L7.72792 1.90455C8.93398 0.698485 10.8894 0.698485 12.0955 1.90455C13.3015 3.11061 13.3015 5.06602 12.0955 6.27208L10.4576 7.9099M3.54237 6.0901L1.90455 7.72792C0.698485 8.93398 0.698485 10.8894 1.90455 12.0955C3.11061 13.3015 5.06602 13.3015 6.27208 12.0955L7.9099 10.4576M4.81623 9.18377L9.18377 4.81623"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
      />
    </SVGIconBase>
  );
};

const ArrowIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  return (
    <SVGIconBase {...props}>
      <path
        d="M10 13L5.35532 8.40105C5.15908 8.20675 5.15752 7.89016 5.35183 7.69393L10 3"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const LeaderIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  const isBold = weight === 'bold';
  return (
    <SVGIconBase {...props}>
      <path
        d={
          isBold
            ? 'M13.5 5.4633L14.2366 5.60463C14.294 5.30554 14.1648 5.00144 13.9097 4.83508C13.6546 4.66873 13.3242 4.67314 13.0737 4.84625L13.5 5.4633ZM10.8916 7.2655L10.4653 6.64845L10.4653 6.64845L10.8916 7.2655ZM10.3416 7.13301L10.9921 6.7597L10.9921 6.7597L10.3416 7.13301ZM7.9697 3L8.62019 2.62669C8.48579 2.3925 8.23588 2.24863 7.96588 2.25001C7.69587 2.25139 7.44744 2.39779 7.31543 2.63334L7.9697 3ZM5.65696 7.12679L5.0027 6.76013L5.65696 7.12679ZM5.10482 7.26302L4.67849 7.88007L4.67849 7.88007L5.10482 7.26302ZM2.5 5.4633L2.92633 4.84626C2.67797 4.67466 2.35095 4.66869 2.0965 4.83109C1.84204 4.9935 1.70975 5.29263 1.7608 5.59014L2.5 5.4633ZM3.36398 12.1667L2.61398 12.1665V12.1667H3.36398ZM12.5333 12.1667L11.7833 12.1665V12.1667H12.5333ZM13.2701 10.6413L14.2366 5.60463L12.7634 5.32197L11.797 10.3587L13.2701 10.6413ZM13.0737 4.84625L10.4653 6.64845L11.3179 7.88254L13.9263 6.08034L13.0737 4.84625ZM10.9921 6.7597L8.62019 2.62669L7.3192 3.37331L9.69109 7.50632L10.9921 6.7597ZM7.31543 2.63334L5.0027 6.76013L6.31123 7.49345L8.62396 3.36666L7.31543 2.63334ZM5.53115 6.64598L2.92633 4.84626L2.07367 6.08034L4.67849 7.88007L5.53115 6.64598ZM1.7608 5.59014L2.62509 10.6268L4.10348 10.3732L3.2392 5.33646L1.7608 5.59014ZM5.0027 6.76013C5.09429 6.5967 5.33739 6.51211 5.53115 6.64598L4.67849 7.88007C5.24646 8.27249 5.99145 8.06406 6.31123 7.49345L5.0027 6.76013ZM10.4653 6.64845C10.6573 6.5158 10.8991 6.59761 10.9921 6.7597L9.69109 7.50632C10.0146 8.07009 10.7544 8.27187 11.3179 7.88254L10.4653 6.64845ZM3.36429 11.25H12.5336V9.75H3.36429V11.25ZM11.7836 10.4999L11.7833 12.1665L13.2833 12.1668L13.2836 10.5001L11.7836 10.4999ZM11.7476 12.25H4.14969V13.75H11.7476V12.25ZM4.11398 12.1668L4.11429 10.5001L2.61429 10.4999L2.61398 12.1665L4.11398 12.1668ZM4.14969 12.25C4.14937 12.25 4.14973 12.25 4.15041 12.2501C4.15104 12.2503 4.15122 12.2504 4.15083 12.2502C4.15001 12.2498 4.14572 12.2477 4.1398 12.2414C4.12775 12.2286 4.11398 12.2039 4.11398 12.1667H2.61398C2.61398 12.9989 3.26057 13.75 4.14969 13.75V12.25ZM11.7833 12.1667C11.7833 12.2039 11.7695 12.2286 11.7574 12.2414C11.7515 12.2477 11.7472 12.2498 11.7464 12.2502C11.746 12.2504 11.7462 12.2503 11.7468 12.2501C11.7475 12.25 11.7479 12.25 11.7476 12.25V13.75C12.6367 13.75 13.2833 12.9989 13.2833 12.1667H11.7833Z'
            : 'M13.5 5.4633L13.991 5.55752C14.0293 5.35813 13.9432 5.15539 13.7731 5.04449C13.6031 4.93358 13.3828 4.93653 13.2158 5.05194L13.5 5.4633ZM10.8916 7.2655L10.6074 6.85413V6.85413L10.8916 7.2655ZM10.3416 7.13301L10.7752 6.88414L10.7752 6.88414L10.3416 7.13301ZM7.9697 3L8.40336 2.75113C8.31376 2.595 8.14716 2.49909 7.96715 2.50001C7.78714 2.50092 7.62152 2.59853 7.53352 2.75556L7.9697 3ZM5.65696 7.12679L5.22079 6.88235L5.22079 6.88235L5.65696 7.12679ZM5.10482 7.26302L4.8206 7.67439H4.8206L5.10482 7.26302ZM2.5 5.4633L2.78422 5.05194C2.61865 4.93754 2.40064 4.93356 2.231 5.04183C2.06136 5.1501 1.97317 5.34952 2.0072 5.54786L2.5 5.4633ZM3.36398 12.1667L2.86398 12.1666V12.1667H3.36398ZM12.5333 12.1667L12.0333 12.1666V12.1667H12.5333ZM13.0246 10.5942L13.991 5.55752L13.009 5.36908L12.0425 10.4058L13.0246 10.5942ZM13.2158 5.05194L10.6074 6.85413L11.1758 7.67686L13.7842 5.87466L13.2158 5.05194ZM10.7752 6.88414L8.40336 2.75113L7.53604 3.24887L9.90792 7.38189L10.7752 6.88414ZM7.53352 2.75556L5.22079 6.88235L6.09314 7.37123L8.40587 3.24444L7.53352 2.75556ZM5.38904 6.85166L2.78422 5.05194L2.21578 5.87466L4.8206 7.67439L5.38904 6.85166ZM2.0072 5.54786L2.87149 10.5846L3.85708 10.4154L2.9928 5.37874L2.0072 5.54786ZM5.22079 6.88235C5.2311 6.86396 5.25414 6.84277 5.2885 6.83429C5.32353 6.82564 5.3617 6.83277 5.38904 6.85166L4.8206 7.67439C5.26162 7.97909 5.84192 7.8195 6.09314 7.37123L5.22079 6.88235ZM10.6074 6.85413C10.6345 6.8354 10.6723 6.82819 10.7072 6.8366C10.7415 6.84485 10.7647 6.86577 10.7752 6.88414L9.90792 7.38188C10.162 7.82468 10.7382 7.97919 11.1758 7.67686L10.6074 6.85413ZM3.36429 11H12.5336V10H3.36429V11ZM12.0336 10.4999L12.0333 12.1666L13.0333 12.1668L13.0336 10.5001L12.0336 10.4999ZM11.7476 12.5H4.14969V13.5H11.7476V12.5ZM3.86398 12.1668L3.86429 10.5001L2.86429 10.4999L2.86398 12.1666L3.86398 12.1668ZM4.14969 12.5C4.01921 12.5 3.86398 12.3789 3.86398 12.1667H2.86398C2.86398 12.8749 3.4123 13.5 4.14969 13.5V12.5ZM12.0333 12.1667C12.0333 12.3789 11.878 12.5 11.7476 12.5V13.5C12.4849 13.5 13.0333 12.8749 13.0333 12.1667H12.0333Z'
        }
      />
    </SVGIconBase>
  );
};

const TopRightFillArrowIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  return (
    <SVGIconBase {...props}>
      <path
        d="M10.9796 9.47287L11 5.33832C11.0009 5.16364 10.8604 5.02133 10.6864 5.02048L6.5225 5M10.9789 5L4 12"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const TopFillArrowIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  return (
    <SVGIconBase {...props}>
      <path
        d="M8 13.5L8 2.75949M11.9375 6.58945L11.375 6.00524L10.25 4.83683L8.72036 3.24816C8.32701 2.83962 7.67299 2.83962 7.27964 3.24816L5.75 4.83683L4.625 6.00524L4.0625 6.58945"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const RefreshIcon: React.FC<SVGIconProps> = ({ weight, ...props }) => {
  return (
    <SVGIconBase {...props}>
      <path
        d="M12.0109 12.3333L11.4884 11.7953L12.0109 12.3333ZM12.0109 3.66667L12.5335 3.12866L12.0109 3.66667ZM12.6792 5.95516C12.8385 6.3375 13.2776 6.51829 13.6599 6.35896C14.0423 6.19963 14.2231 5.76052 14.0638 5.37818L12.6792 5.95516ZM14.0638 10.6218C14.2231 10.2395 14.0423 9.80037 13.6599 9.64104C13.2776 9.48171 12.8385 9.6625 12.6792 10.0448L14.0638 10.6218ZM14.75 3.33333V2.58333H13.25V3.33333H14.75ZM11.3699 5.25H10.6199V6.75H11.3699V5.25ZM7.91781 13.25C5.07343 13.25 2.75 10.9093 2.75 8H1.25C1.25 11.7181 4.22556 14.75 7.91781 14.75V13.25ZM2.75 8C2.75 5.09072 5.07343 2.75 7.91781 2.75V1.25C4.22556 1.25 1.25 4.28186 1.25 8H2.75ZM11.4884 11.7953C10.5587 12.6982 9.30171 13.25 7.91781 13.25V14.75C9.71023 14.75 11.3373 14.0332 12.5335 12.8713L11.4884 11.7953ZM7.91781 2.75C9.30171 2.75 10.5587 3.30175 11.4884 4.20467L12.5335 3.12866C11.3373 1.96685 9.71023 1.25 7.91781 1.25V2.75ZM11.4884 4.20467C11.9944 4.69609 12.4023 5.29075 12.6792 5.95516L14.0638 5.37818C13.708 4.52438 13.1839 3.76036 12.5335 3.12866L11.4884 4.20467ZM12.6792 10.0448C12.4023 10.7093 11.9944 11.3039 11.4884 11.7953L12.5335 12.8713C13.1839 12.2396 13.708 11.4756 14.0638 10.6218L12.6792 10.0448ZM13.25 3.33333V5.5H14.75V3.33333H13.25ZM13.5 5.25H11.3699V6.75H13.5V5.25ZM13.25 5.5C13.25 5.36161 13.3626 5.25 13.5 5.25V6.75C14.1897 6.75 14.75 6.19135 14.75 5.5H13.25Z"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const PlusIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M3 8H8M8 8H13M8 8V3M8 8V13"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};
const MinusIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path d="M2 8H8H14" strokeWidth={weight === 'bold' ? 1.5 : 1} strokeLinecap="round" strokeLinejoin="round" />
    </SVGIconBase>
  );
};

const ProfileIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M8 8.75C9.65685 8.75 11 7.40685 11 5.75C11 4.09315 9.65685 2.75 8 2.75C6.34315 2.75 5 4.09315 5 5.75C5 7.40685 6.34315 8.75 8 8.75ZM8 8.75C5.23858 8.75 3 10.4886 3 13.25M8 8.75C10.7614 8.75 13 10.4886 13 13.25"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
      />
    </SVGIconBase>
  );
};

const BellIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M9.75 12.7422V13.0886C9.75 14.0551 8.9665 14.8386 8 14.8386C7.0335 14.8386 6.25 14.0551 6.25 13.0886V12.7422"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
      />
      <path
        d="M3.79976 6.63359C3.79976 4.314 5.68016 2.43359 7.99976 2.43359V2.43359C10.3194 2.43359 12.1998 4.314 12.1998 6.63359V9.60012C12.1998 9.61382 12.2037 9.62722 12.2111 9.63876L13.3809 11.4666C13.6485 11.8848 13.3482 12.4336 12.8516 12.4336H3.14788C2.65134 12.4336 2.351 11.8848 2.61866 11.4666L3.78845 9.63876C3.79583 9.62722 3.79976 9.61382 3.79976 9.60012V6.63359Z"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinejoin="round"
      />
      <path d="M8 1V2.12903" strokeWidth={weight === 'bold' ? 1.5 : 1} strokeLinecap="round" />
    </SVGIconBase>
  );
};

const CloseIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M3.5 12.5L8 8M8 8L12.5 3.5M8 8L3.5 3.5M8 8L12.5 12.5"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const CheckIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M3 7.75L6.14645 10.8964C6.34171 11.0917 6.65829 11.0917 6.85355 10.8964L13 4.75"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const CalendarIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight = 'normal', ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <svg width={props.size} height={props.size} viewBox="0 0 16 16" fill="none" className={props.className}>
          <path
            d="M2.5 3.79839C2.5 3.2043 2.96256 2.75 3.5 2.75H12.5C13.0374 2.75 13.5 3.2043 13.5 3.79839V12.7016C13.5 13.2957 13.0374 13.75 12.5 13.75H3.5C2.96256 13.75 2.5 13.2957 2.5 12.7016V3.79839Z"
            stroke="#4C5057"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.5 1.75V2.75"
            stroke="#4C5057"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path
            d="M10.5 1.75V2.75"
            stroke="#4C5057"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path d="M3 6.5H13" stroke="#4C5057" strokeWidth={`${weight === 'bold' ? 1.5 : 1}`} strokeLinecap="round" />
          <path
            d="M6.15624 9.25C6.15624 9.66421 5.82045 10 5.40624 10C4.99203 10 4.65625 9.66421 4.65625 9.25C4.65625 8.83579 4.99203 8.5 5.40624 8.5C5.82045 8.5 6.15624 8.83579 6.15624 9.25Z"
            fill="#4C5057"
          />
          <path
            d="M8.74997 9.25C8.74997 9.66421 8.41419 10 7.99998 10C7.58577 10 7.24998 9.66421 7.24998 9.25C7.24998 8.83579 7.58577 8.5 7.99998 8.5C8.41419 8.5 8.74997 8.83579 8.74997 9.25Z"
            fill="#4C5057"
          />
          <path
            d="M11.3437 9.25C11.3437 9.66421 11.0079 10 10.5937 10C10.1795 10 9.84371 9.66421 9.84371 9.25C9.84371 8.83579 10.1795 8.5 10.5937 8.5C11.0079 8.5 11.3437 8.83579 11.3437 9.25Z"
            fill="#4C5057"
          />
        </svg>
      );
    case 'gray400':
      return (
        <svg width={props.size} height={props.size} viewBox="0 0 16 16" fill="none" className={props.className}>
          <path
            d="M2.5 3.79839C2.5 3.2043 2.96256 2.75 3.5 2.75H12.5C13.0374 2.75 13.5 3.2043 13.5 3.79839V12.7016C13.5 13.2957 13.0374 13.75 12.5 13.75H3.5C2.96256 13.75 2.5 13.2957 2.5 12.7016V3.79839Z"
            stroke="#C7CBD1"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.5 1.75V2.75"
            stroke="#C7CBD1"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path
            d="M10.5 1.75V2.75"
            stroke="#C7CBD1"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path d="M3 6.5H13" stroke="#C7CBD1" strokeWidth={`${weight === 'bold' ? 1.5 : 1}`} strokeLinecap="round" />
          <path
            d="M6.15624 9.25C6.15624 9.66421 5.82045 10 5.40624 10C4.99203 10 4.65625 9.66421 4.65625 9.25C4.65625 8.83579 4.99203 8.5 5.40624 8.5C5.82045 8.5 6.15624 8.83579 6.15624 9.25Z"
            fill="#C7CBD1"
          />
          <path
            d="M8.74997 9.25C8.74997 9.66421 8.41419 10 7.99998 10C7.58577 10 7.24998 9.66421 7.24998 9.25C7.24998 8.83579 7.58577 8.5 7.99998 8.5C8.41419 8.5 8.74997 8.83579 8.74997 9.25Z"
            fill="#C7CBD1"
          />
          <path
            d="M11.3437 9.25C11.3437 9.66421 11.0079 10 10.5937 10C10.1795 10 9.84371 9.66421 9.84371 9.25C9.84371 8.83579 10.1795 8.5 10.5937 8.5C11.0079 8.5 11.3437 8.83579 11.3437 9.25Z"
            fill="#C7CBD1"
          />
        </svg>
      );
    case 'orange800':
      return (
        <svg width={props.size} height={props.size} viewBox="0 0 16 16" fill="none" className={props.className}>
          <path
            d="M2.5 3.79839C2.5 3.2043 2.96256 2.75 3.5 2.75H12.5C13.0374 2.75 13.5 3.2043 13.5 3.79839V12.7016C13.5 13.2957 13.0374 13.75 12.5 13.75H3.5C2.96256 13.75 2.5 13.2957 2.5 12.7016V3.79839Z"
            stroke="#FF600C"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.5 1.75V2.75"
            stroke="#FF600C"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path
            d="M10.5 1.75V2.75"
            stroke="#FF600C"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path d="M3 6.5H13" stroke="#FF600C" strokeWidth={`${weight === 'bold' ? 1.5 : 1}`} strokeLinecap="round" />
          <path
            d="M6.15624 9.25C6.15624 9.66421 5.82045 10 5.40624 10C4.99203 10 4.65625 9.66421 4.65625 9.25C4.65625 8.83579 4.99203 8.5 5.40624 8.5C5.82045 8.5 6.15624 8.83579 6.15624 9.25Z"
            fill="#FF600C"
          />
          <path
            d="M8.74997 9.25C8.74997 9.66421 8.41419 10 7.99998 10C7.58577 10 7.24998 9.66421 7.24998 9.25C7.24998 8.83579 7.58577 8.5 7.99998 8.5C8.41419 8.5 8.74997 8.83579 8.74997 9.25Z"
            fill="#FF600C"
          />
          <path
            d="M11.3437 9.25C11.3437 9.66421 11.0079 10 10.5937 10C10.1795 10 9.84371 9.66421 9.84371 9.25C9.84371 8.83579 10.1795 8.5 10.5937 8.5C11.0079 8.5 11.3437 8.83579 11.3437 9.25Z"
            fill="#FF600C"
          />
        </svg>
      );
    case 'white':
      return (
        <svg width={props.size} height={props.size} viewBox="0 0 16 16" fill="none" className={props.className}>
          <path
            d="M2.5 3.79839C2.5 3.2043 2.96256 2.75 3.5 2.75H12.5C13.0374 2.75 13.5 3.2043 13.5 3.79839V12.7016C13.5 13.2957 13.0374 13.75 12.5 13.75H3.5C2.96256 13.75 2.5 13.2957 2.5 12.7016V3.79839Z"
            stroke="white"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.5 1.75V2.75"
            stroke="white"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path
            d="M10.5 1.75V2.75"
            stroke="white"
            strokeWidth={`${weight === 'bold' ? 1.5 : 1}`}
            strokeLinecap="round"
          />
          <path d="M3 6.5H13" stroke="white" strokeWidth={`${weight === 'bold' ? 1.5 : 1}`} strokeLinecap="round" />
          <path
            d="M6.15624 9.25C6.15624 9.66421 5.82045 10 5.40624 10C4.99203 10 4.65625 9.66421 4.65625 9.25C4.65625 8.83579 4.99203 8.5 5.40624 8.5C5.82045 8.5 6.15624 8.83579 6.15624 9.25Z"
            fill="white"
          />
          <path
            d="M8.74997 9.25C8.74997 9.66421 8.41419 10 7.99998 10C7.58577 10 7.24998 9.66421 7.24998 9.25C7.24998 8.83579 7.58577 8.5 7.99998 8.5C8.41419 8.5 8.74997 8.83579 8.74997 9.25Z"
            fill="white"
          />
          <path
            d="M11.3437 9.25C11.3437 9.66421 11.0079 10 10.5937 10C10.1795 10 9.84371 9.66421 9.84371 9.25C9.84371 8.83579 10.1795 8.5 10.5937 8.5C11.0079 8.5 11.3437 8.83579 11.3437 9.25Z"
            fill="white"
          />
        </svg>
      );
    default:
      return null;
  }
};

const NoteIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M3.5 2.75H12.5C13.0374 2.75 13.5 3.2043 13.5 3.79839V12.7016C13.5 13.2957 13.0374 13.75 12.5 13.75H3.5C2.96256 13.75 2.5 13.2957 2.5 12.7016V3.79839C2.5 3.2043 2.96256 2.75 3.5 2.75Z"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.25 1.75V2.75M10.75 1.75V2.75M8 1.75V2.75"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M5 6H10" strokeWidth={weight === 'bold' ? 1.5 : 1} strokeLinecap="round" />
      <path d="M5 8.25H8" strokeWidth={weight === 'bold' ? 1.5 : 1} strokeLinecap="round" />
      <path d="M5 10.5H11" strokeWidth={weight === 'bold' ? 1.5 : 1} strokeLinecap="round" />
    </SVGIconBase>
  );
};

const BulletIcon: React.FC<SVGIconProps> = ({ color = 'gray700', ...props }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="2" height="20" viewBox="0 0 2 20" fill="none">
      <circle cx="1" cy="10" r="1" fill={getStrokeColor(color)} />
    </svg>
  );
};

const HomeIcon: React.FC<SVGIconProps> = ({ weight, color = 'gray700', ...props }) => {
  return (
    <SVGIconBase color={color} {...props}>
      <path
        d="M2 7.2447V12.9799C2 13.5433 2.44771 14 3 14H13C13.5523 14 14 13.5433 14 12.9799V7.2447C14 6.59157 13.6934 5.97793 13.1754 5.59409L8.58771 2.19476C8.23725 1.93508 7.76275 1.93508 7.41229 2.19476L2.82459 5.59409C2.30655 5.97793 2 6.59157 2 7.2447Z"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 9.40971C6 8.84634 6.44772 8.38965 7 8.38965H9C9.55228 8.38965 10 8.84634 10 9.40971V14H6V9.40971Z"
        strokeWidth={weight === 'bold' ? 1.5 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SVGIconBase>
  );
};

const BellAlertIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight, ...props }) => {
  const isBold = weight === 'bold';
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase {...props}>
          {isBold ? (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75003 1C8.75003 0.585786 8.41424 0.25 8.00003 0.25C7.58582 0.25 7.25003 0.585786 7.25003 1V1.74001C4.872 2.10133 3.04979 4.15466 3.04979 6.63359V9.40164L1.98698 11.0623C1.39983 11.9797 2.05868 13.1836 3.14791 13.1836H5.5018C5.55175 14.5203 6.65113 15.5886 8.00003 15.5886C9.34893 15.5886 10.4483 14.5203 10.4983 13.1836H12.8517C13.9409 13.1836 14.5997 11.9797 14.0126 11.0623L12.9498 9.40164V6.84654C12.6513 6.94609 12.3319 7 12 7C11.8121 7 11.6282 6.98272 11.4498 6.94966V9.60012C11.4498 9.75709 11.4947 9.9108 11.5794 10.043L12.6293 11.6836H3.37023L4.42019 10.043C4.50481 9.91081 4.54979 9.75711 4.54979 9.60012V6.63359C4.54979 4.72821 6.0944 3.18359 7.99979 3.18359C8.37336 3.18359 8.73307 3.24297 9.06999 3.3528C9.18211 2.84291 9.42393 2.38165 9.75911 2.00536C9.43746 1.88302 9.09976 1.79326 8.75003 1.74008V1ZM8.00003 14.0886C7.47979 14.0886 7.05234 13.6913 7.00448 13.1836H8.99557C8.94772 13.6913 8.52027 14.0886 8.00003 14.0886Z"
                fill="#4C5057"
              />
              <path
                d="M13.5 4C13.5 4.82843 12.8284 5.5 12 5.5C11.1716 5.5 10.5 4.82843 10.5 4C10.5 3.17157 11.1716 2.5 12 2.5C12.8284 2.5 13.5 3.17157 13.5 4Z"
                fill="#FF2626"
              />
            </>
          ) : (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.99987 0.5C8.27601 0.5 8.49987 0.723858 8.49987 1V1.9599C8.80933 1.99264 9.10984 2.05542 9.39841 2.14526C9.21846 2.42382 9.09239 2.74044 9.03494 3.08039C8.7064 2.98483 8.359 2.93359 7.99962 2.93359C5.95617 2.93359 4.29962 4.59014 4.29962 6.63359V9.60012C4.29962 9.70935 4.26833 9.81629 4.20945 9.90828L3.03966 11.7361C2.98499 11.8215 3.04633 11.9336 3.14775 11.9336H12.8515C12.9529 11.9336 13.0143 11.8215 12.9596 11.7361L11.7898 9.90828C11.7309 9.81629 11.6996 9.70935 11.6996 9.60012V6.63359C11.6996 6.41612 11.6809 6.20304 11.6449 5.99586C11.9917 5.97603 12.3197 5.88547 12.6145 5.7384C12.6704 6.0282 12.6996 6.32748 12.6996 6.63359V9.47479L13.8019 11.197C14.2825 11.9481 13.7432 12.9336 12.8515 12.9336H10.2499V13.0886C10.2499 14.3312 9.24251 15.3386 7.99987 15.3386C6.75723 15.3386 5.74987 14.3312 5.74987 13.0886V12.9336H3.14775C2.25608 12.9336 1.71673 11.9481 2.19739 11.197L3.29962 9.47479V6.63359C3.29962 4.20667 5.13909 2.20937 7.49987 1.95985V1C7.49987 0.723858 7.72372 0.5 7.99987 0.5ZM6.74987 12.9336V13.0886C6.74987 13.7789 7.30951 14.3386 7.99987 14.3386C8.69022 14.3386 9.24987 13.7789 9.24987 13.0886V12.9336H6.74987Z"
                fill="#4C5057"
              />
              <path
                d="M13 3.5C13 4.32843 12.3284 5 11.5 5C10.6716 5 10 4.32843 10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5Z"
                fill="#FF2626"
              />
            </>
          )}
        </SVGIconBase>
      );

    case 'gray400':
      return (
        <SVGIconBase {...props}>
          {isBold ? (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75003 1C8.75003 0.585786 8.41424 0.25 8.00003 0.25C7.58582 0.25 7.25003 0.585786 7.25003 1V1.74001C4.872 2.10133 3.04979 4.15466 3.04979 6.63359V9.40164L1.98698 11.0623C1.39983 11.9797 2.05868 13.1836 3.14791 13.1836H5.5018C5.55175 14.5203 6.65113 15.5886 8.00003 15.5886C9.34893 15.5886 10.4483 14.5203 10.4983 13.1836H12.8517C13.9409 13.1836 14.5997 11.9797 14.0126 11.0623L12.9498 9.40164V6.84654C12.6513 6.94609 12.3319 7 12 7C11.8121 7 11.6282 6.98272 11.4498 6.94966V9.60012C11.4498 9.75709 11.4947 9.9108 11.5794 10.043L12.6293 11.6836H3.37023L4.42019 10.043C4.50481 9.91081 4.54979 9.75711 4.54979 9.60012V6.63359C4.54979 4.72821 6.0944 3.18359 7.99979 3.18359C8.37336 3.18359 8.73307 3.24297 9.06999 3.3528C9.18211 2.84291 9.42393 2.38165 9.75911 2.00536C9.43746 1.88302 9.09976 1.79326 8.75003 1.74008V1ZM8.00003 14.0886C7.47979 14.0886 7.05234 13.6913 7.00448 13.1836H8.99557C8.94772 13.6913 8.52027 14.0886 8.00003 14.0886Z"
                fill="#C7CBD1"
              />
              <path
                d="M13.5 4C13.5 4.82843 12.8284 5.5 12 5.5C11.1716 5.5 10.5 4.82843 10.5 4C10.5 3.17157 11.1716 2.5 12 2.5C12.8284 2.5 13.5 3.17157 13.5 4Z"
                fill="#FF2626"
              />
            </>
          ) : (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.99987 0.5C8.27601 0.5 8.49987 0.723858 8.49987 1V1.9599C8.80933 1.99264 9.10984 2.05542 9.39841 2.14526C9.21846 2.42382 9.09239 2.74044 9.03494 3.08039C8.7064 2.98483 8.359 2.93359 7.99962 2.93359C5.95617 2.93359 4.29962 4.59014 4.29962 6.63359V9.60012C4.29962 9.70935 4.26833 9.81629 4.20945 9.90828L3.03966 11.7361C2.98499 11.8215 3.04633 11.9336 3.14775 11.9336H12.8515C12.9529 11.9336 13.0143 11.8215 12.9596 11.7361L11.7898 9.90828C11.7309 9.81629 11.6996 9.70935 11.6996 9.60012V6.63359C11.6996 6.41612 11.6809 6.20304 11.6449 5.99586C11.9917 5.97603 12.3197 5.88547 12.6145 5.7384C12.6704 6.0282 12.6996 6.32748 12.6996 6.63359V9.47479L13.8019 11.197C14.2825 11.9481 13.7432 12.9336 12.8515 12.9336H10.2499V13.0886C10.2499 14.3312 9.24251 15.3386 7.99987 15.3386C6.75723 15.3386 5.74987 14.3312 5.74987 13.0886V12.9336H3.14775C2.25608 12.9336 1.71673 11.9481 2.19739 11.197L3.29962 9.47479V6.63359C3.29962 4.20667 5.13909 2.20937 7.49987 1.95985V1C7.49987 0.723858 7.72372 0.5 7.99987 0.5ZM6.74987 12.9336V13.0886C6.74987 13.7789 7.30951 14.3386 7.99987 14.3386C8.69022 14.3386 9.24987 13.7789 9.24987 13.0886V12.9336H6.74987Z"
                fill="#C7CBD1"
              />
              <path
                d="M13 3.5C13 4.32843 12.3284 5 11.5 5C10.6716 5 10 4.32843 10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5Z"
                fill="#FF2626"
              />
            </>
          )}
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase {...props}>
          {isBold ? (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75003 1C8.75003 0.585786 8.41424 0.25 8.00003 0.25C7.58582 0.25 7.25003 0.585786 7.25003 1V1.74001C4.872 2.10133 3.04979 4.15466 3.04979 6.63359V9.40164L1.98698 11.0623C1.39983 11.9797 2.05868 13.1836 3.14791 13.1836H5.5018C5.55175 14.5203 6.65113 15.5886 8.00003 15.5886C9.34893 15.5886 10.4483 14.5203 10.4983 13.1836H12.8517C13.9409 13.1836 14.5997 11.9797 14.0126 11.0623L12.9498 9.40164V6.84654C12.6513 6.94609 12.3319 7 12 7C11.8121 7 11.6282 6.98272 11.4498 6.94966V9.60012C11.4498 9.75709 11.4947 9.9108 11.5794 10.043L12.6293 11.6836H3.37023L4.42019 10.043C4.50481 9.91081 4.54979 9.75711 4.54979 9.60012V6.63359C4.54979 4.72821 6.0944 3.18359 7.99979 3.18359C8.37336 3.18359 8.73307 3.24297 9.06999 3.3528C9.18211 2.84291 9.42393 2.38165 9.75911 2.00536C9.43746 1.88302 9.09976 1.79326 8.75003 1.74008V1ZM8.00003 14.0886C7.47979 14.0886 7.05234 13.6913 7.00448 13.1836H8.99557C8.94772 13.6913 8.52027 14.0886 8.00003 14.0886Z"
                fill="#FF600C"
              />
              <path
                d="M13.5 4C13.5 4.82843 12.8284 5.5 12 5.5C11.1716 5.5 10.5 4.82843 10.5 4C10.5 3.17157 11.1716 2.5 12 2.5C12.8284 2.5 13.5 3.17157 13.5 4Z"
                fill="#FF2626"
              />
            </>
          ) : (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.99987 0.5C8.27601 0.5 8.49987 0.723858 8.49987 1V1.9599C8.80933 1.99264 9.10984 2.05542 9.39841 2.14526C9.21846 2.42382 9.09239 2.74044 9.03494 3.08039C8.7064 2.98483 8.359 2.93359 7.99962 2.93359C5.95617 2.93359 4.29962 4.59014 4.29962 6.63359V9.60012C4.29962 9.70935 4.26833 9.81629 4.20945 9.90828L3.03966 11.7361C2.98499 11.8215 3.04633 11.9336 3.14775 11.9336H12.8515C12.9529 11.9336 13.0143 11.8215 12.9596 11.7361L11.7898 9.90828C11.7309 9.81629 11.6996 9.70935 11.6996 9.60012V6.63359C11.6996 6.41612 11.6809 6.20304 11.6449 5.99586C11.9917 5.97603 12.3197 5.88547 12.6145 5.7384C12.6704 6.0282 12.6996 6.32748 12.6996 6.63359V9.47479L13.8019 11.197C14.2825 11.9481 13.7432 12.9336 12.8515 12.9336H10.2499V13.0886C10.2499 14.3312 9.24251 15.3386 7.99987 15.3386C6.75723 15.3386 5.74987 14.3312 5.74987 13.0886V12.9336H3.14775C2.25608 12.9336 1.71673 11.9481 2.19739 11.197L3.29962 9.47479V6.63359C3.29962 4.20667 5.13909 2.20937 7.49987 1.95985V1C7.49987 0.723858 7.72372 0.5 7.99987 0.5ZM6.74987 12.9336V13.0886C6.74987 13.7789 7.30951 14.3386 7.99987 14.3386C8.69022 14.3386 9.24987 13.7789 9.24987 13.0886V12.9336H6.74987Z"
                fill="#FF600C"
              />
              <path
                d="M13 3.5C13 4.32843 12.3284 5 11.5 5C10.6716 5 10 4.32843 10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5Z"
                fill="#FF2626"
              />
            </>
          )}
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase {...props}>
          {isBold ? (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75003 1C8.75003 0.585786 8.41424 0.25 8.00003 0.25C7.58582 0.25 7.25003 0.585786 7.25003 1V1.74001C4.872 2.10133 3.04979 4.15466 3.04979 6.63359V9.40164L1.98698 11.0623C1.39983 11.9797 2.05868 13.1836 3.14791 13.1836H5.5018C5.55175 14.5203 6.65113 15.5886 8.00003 15.5886C9.34893 15.5886 10.4483 14.5203 10.4983 13.1836H12.8517C13.9409 13.1836 14.5997 11.9797 14.0126 11.0623L12.9498 9.40164V6.84654C12.6513 6.94609 12.3319 7 12 7C11.8121 7 11.6282 6.98272 11.4498 6.94966V9.60012C11.4498 9.75709 11.4947 9.9108 11.5794 10.043L12.6293 11.6836H3.37023L4.42019 10.043C4.50481 9.91081 4.54979 9.75711 4.54979 9.60012V6.63359C4.54979 4.72821 6.0944 3.18359 7.99979 3.18359C8.37336 3.18359 8.73307 3.24297 9.06999 3.3528C9.18211 2.84291 9.42393 2.38165 9.75911 2.00536C9.43746 1.88302 9.09976 1.79326 8.75003 1.74008V1ZM8.00003 14.0886C7.47979 14.0886 7.05234 13.6913 7.00448 13.1836H8.99557C8.94772 13.6913 8.52027 14.0886 8.00003 14.0886Z"
                fill="white"
              />
              <path
                d="M13.5 4C13.5 4.82843 12.8284 5.5 12 5.5C11.1716 5.5 10.5 4.82843 10.5 4C10.5 3.17157 11.1716 2.5 12 2.5C12.8284 2.5 13.5 3.17157 13.5 4Z"
                fill="#FF2626"
              />
            </>
          ) : (
            <>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.99987 0.5C8.27601 0.5 8.49987 0.723858 8.49987 1V1.9599C8.80933 1.99264 9.10984 2.05542 9.39841 2.14526C9.21846 2.42382 9.09239 2.74044 9.03494 3.08039C8.7064 2.98483 8.359 2.93359 7.99962 2.93359C5.95617 2.93359 4.29962 4.59014 4.29962 6.63359V9.60012C4.29962 9.70935 4.26833 9.81629 4.20945 9.90828L3.03966 11.7361C2.98499 11.8215 3.04633 11.9336 3.14775 11.9336H12.8515C12.9529 11.9336 13.0143 11.8215 12.9596 11.7361L11.7898 9.90828C11.7309 9.81629 11.6996 9.70935 11.6996 9.60012V6.63359C11.6996 6.41612 11.6809 6.20304 11.6449 5.99586C11.9917 5.97603 12.3197 5.88547 12.6145 5.7384C12.6704 6.0282 12.6996 6.32748 12.6996 6.63359V9.47479L13.8019 11.197C14.2825 11.9481 13.7432 12.9336 12.8515 12.9336H10.2499V13.0886C10.2499 14.3312 9.24251 15.3386 7.99987 15.3386C6.75723 15.3386 5.74987 14.3312 5.74987 13.0886V12.9336H3.14775C2.25608 12.9336 1.71673 11.9481 2.19739 11.197L3.29962 9.47479V6.63359C3.29962 4.20667 5.13909 2.20937 7.49987 1.95985V1C7.49987 0.723858 7.72372 0.5 7.99987 0.5ZM6.74987 12.9336V13.0886C6.74987 13.7789 7.30951 14.3386 7.99987 14.3386C8.69022 14.3386 9.24987 13.7789 9.24987 13.0886V12.9336H6.74987Z"
                fill="white"
              />
              <path
                d="M13 3.5C13 4.32843 12.3284 5 11.5 5C10.6716 5 10 4.32843 10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5Z"
                fill="#FF2626"
              />
            </>
          )}
        </SVGIconBase>
      );
    default:
      return null;
  }
};

const SearchIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight = 'normal', ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="6.75" cy="6.75" r="4.5" stroke="#4C5057" strokeWidth="1.5" />
              <path
                d="M10.25 10.25L13.5 13.5"
                stroke="#4C5057"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <circle cx="6.75" cy="6.75" r="4.75" stroke="#4C5057" />
              <path d="M10.25 10.25L13.5 13.5" stroke="#4C5057" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
        </SVGIconBase>
      );
    case 'gray400':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="6.75" cy="6.75" r="4.5" stroke="#C7CBD1" strokeWidth="1.5" />
              <path
                d="M10.25 10.25L13.5 13.5"
                stroke="#C7CBD1"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <circle cx="6.75" cy="6.75" r="4.75" stroke="#C7CBD1" />
              <path d="M10.25 10.25L13.5 13.5" stroke="#C7CBD1" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="6.75" cy="6.75" r="4.5" stroke="#FF600C" strokeWidth="1.5" />
              <path
                d="M10.25 10.25L13.5 13.5"
                stroke="#FF600C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <circle cx="6.75" cy="6.75" r="4.75" stroke="#FF600C" />
              <path d="M10.25 10.25L13.5 13.5" stroke="#FF600C" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="6.75" cy="6.75" r="4.5" stroke="white" strokeWidth="1.5" />
              <path
                d="M10.25 10.25L13.5 13.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <circle cx="6.75" cy="6.75" r="4.75" stroke="white" />
              <path d="M10.25 10.25L13.5 13.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
        </SVGIconBase>
      );
    default:
      return null;
  }
};
const TalkIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight = 'normal', ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <path
                d="M1.5246 12.6456L2.28715 10.4059C1.87303 9.66731 1.65604 8.84263 1.65538 8.00487C1.65538 4.93059 4.53657 2.42871 8.07769 2.42871C11.6188 2.42871 14.5 4.93059 14.5 8.00487C14.5 11.0743 11.6198 13.5714 8.07769 13.5714C6.77134 13.5796 5.49019 13.2282 4.38768 12.5592L2.15838 13.2507C2.07016 13.278 1.97574 13.2815 1.88555 13.2609C1.79536 13.2402 1.71291 13.1961 1.64732 13.1335C1.58174 13.0709 1.53556 12.9922 1.5139 12.9061C1.49225 12.82 1.49595 12.7298 1.5246 12.6456Z"
                stroke="#4C5057"
                strokeWidth="1.39286"
                strokeLinejoin="round"
              />
              <path
                d="M5.9781 8.11601C5.9781 8.55115 5.62536 8.9039 5.19022 8.9039C4.75509 8.9039 4.40234 8.55115 4.40234 8.11601C4.40234 7.68087 4.75509 7.32812 5.19022 7.32812C5.62536 7.32812 5.9781 7.68087 5.9781 8.11601Z"
                fill="#4C5057"
                stroke="none"
              />
              <path
                d="M8.90389 8.11601C8.90389 8.55115 8.55114 8.9039 8.11601 8.9039C7.68087 8.9039 7.32812 8.55115 7.32812 8.11601C7.32812 7.68087 7.68087 7.32812 8.11601 7.32812C8.55114 7.32812 8.90389 7.68087 8.90389 8.11601Z"
                fill="#4C5057"
                stroke="none"
              />
              <path
                d="M11.8316 8.11601C11.8316 8.55115 11.4789 8.9039 11.0437 8.9039C10.6086 8.9039 10.2559 8.55115 10.2559 8.11601C10.2559 7.68087 10.6086 7.32812 11.0437 7.32812C11.4789 7.32812 11.8316 7.68087 11.8316 8.11601Z"
                fill="#4C5057"
                stroke="none"
              />
            </>
          ) : (
            <>
              <path
                d="M2.28383 10.3463L2.3929 10.5408L2.32102 10.752L1.50037 13.1623C1.50092 13.1632 1.50198 13.1647 1.50392 13.1666C1.50806 13.1705 1.51567 13.1755 1.52694 13.178C1.53826 13.1806 1.55029 13.1801 1.5609 13.1768C1.56091 13.1768 1.56093 13.1768 1.56095 13.1768L3.96168 12.4322L4.17672 12.3655L4.36919 12.4823C5.47633 13.1541 6.76495 13.5081 8.08053 13.4999L8.08053 13.4998H8.08367C11.6916 13.4998 14.5 10.9698 14.5 8.0051C14.5 5.03445 11.6897 2.5 8.08367 2.5C4.47781 2.5 1.66758 5.03422 1.66733 8.0047C1.66798 8.82044 1.8792 9.62462 2.28383 10.3463ZM1.49985 13.1638L1.49981 13.1639L1.49542 13.1624L1.49541 13.1624L1.02649 13.0028L1.49985 13.1638Z"
                stroke="#4C5057"
              />
              <circle cx="5" cy="8" r="0.75" fill="#4C5057" stroke="none" />
              <circle cx="8" cy="8" r="0.75" fill="#4C5057" stroke="none" />
              <circle cx="11" cy="8" r="0.75" fill="#4C5057" stroke="none" />
            </>
          )}
        </SVGIconBase>
      );
    case 'gray400':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <path
                d="M1.5246 12.6456L2.28715 10.4059C1.87303 9.66731 1.65604 8.84263 1.65538 8.00487C1.65538 4.93059 4.53657 2.42871 8.07769 2.42871C11.6188 2.42871 14.5 4.93059 14.5 8.00487C14.5 11.0743 11.6198 13.5714 8.07769 13.5714C6.77134 13.5796 5.49019 13.2282 4.38768 12.5592L2.15838 13.2507C2.07016 13.278 1.97574 13.2815 1.88555 13.2609C1.79536 13.2402 1.71291 13.1961 1.64732 13.1335C1.58174 13.0709 1.53556 12.9922 1.5139 12.9061C1.49225 12.82 1.49595 12.7298 1.5246 12.6456Z"
                stroke="#C7CBD1"
                strokeWidth="1.39286"
                strokeLinejoin="round"
              />
              <path
                d="M5.9781 8.11601C5.9781 8.55115 5.62536 8.9039 5.19022 8.9039C4.75509 8.9039 4.40234 8.55115 4.40234 8.11601C4.40234 7.68087 4.75509 7.32812 5.19022 7.32812C5.62536 7.32812 5.9781 7.68087 5.9781 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
              <path
                d="M8.90389 8.11601C8.90389 8.55115 8.55114 8.9039 8.11601 8.9039C7.68087 8.9039 7.32812 8.55115 7.32812 8.11601C7.32812 7.68087 7.68087 7.32812 8.11601 7.32812C8.55114 7.32812 8.90389 7.68087 8.90389 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
              <path
                d="M11.8316 8.11601C11.8316 8.55115 11.4789 8.9039 11.0437 8.9039C10.6086 8.9039 10.2559 8.55115 10.2559 8.11601C10.2559 7.68087 10.6086 7.32812 11.0437 7.32812C11.4789 7.32812 11.8316 7.68087 11.8316 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
            </>
          ) : (
            <>
              <path
                d="M2.28383 10.3463L2.3929 10.5408L2.32102 10.752L1.50037 13.1623C1.50092 13.1632 1.50198 13.1647 1.50392 13.1666C1.50806 13.1705 1.51567 13.1755 1.52694 13.178C1.53826 13.1806 1.55029 13.1801 1.5609 13.1768C1.56091 13.1768 1.56093 13.1768 1.56095 13.1768L3.96168 12.4322L4.17672 12.3655L4.36919 12.4823C5.47633 13.1541 6.76495 13.5081 8.08053 13.4999L8.08053 13.4998H8.08367C11.6916 13.4998 14.5 10.9698 14.5 8.0051C14.5 5.03445 11.6897 2.5 8.08367 2.5C4.47781 2.5 1.66758 5.03422 1.66733 8.0047C1.66798 8.82044 1.8792 9.62462 2.28383 10.3463ZM1.49985 13.1638L1.49981 13.1639L1.49542 13.1624L1.49541 13.1624L1.02649 13.0028L1.49985 13.1638Z"
                stroke="#C7CBD1"
              />
              <path
                xmlns="http://www.w3.org/2000/svg"
                d="M5.9781 8.11601C5.9781 8.55115 5.62536 8.9039 5.19022 8.9039C4.75509 8.9039 4.40234 8.55115 4.40234 8.11601C4.40234 7.68087 4.75509 7.32812 5.19022 7.32812C5.62536 7.32812 5.9781 7.68087 5.9781 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
              <path
                xmlns="http://www.w3.org/2000/svg"
                d="M8.90389 8.11601C8.90389 8.55115 8.55114 8.9039 8.11601 8.9039C7.68087 8.9039 7.32812 8.55115 7.32812 8.11601C7.32812 7.68087 7.68087 7.32812 8.11601 7.32812C8.55114 7.32812 8.90389 7.68087 8.90389 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
              <path
                xmlns="http://www.w3.org/2000/svg"
                d="M11.8316 8.11601C11.8316 8.55115 11.4789 8.9039 11.0437 8.9039C10.6086 8.9039 10.2559 8.55115 10.2559 8.11601C10.2559 7.68087 10.6086 7.32812 11.0437 7.32812C11.4789 7.32812 11.8316 7.68087 11.8316 8.11601Z"
                fill="#C7CBD1"
                stroke="none"
              />
            </>
          )}
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <path
                d="M1.5246 12.6456L2.28715 10.4059C1.87303 9.66731 1.65604 8.84263 1.65538 8.00487C1.65538 4.93059 4.53657 2.42871 8.07769 2.42871C11.6188 2.42871 14.5 4.93059 14.5 8.00487C14.5 11.0743 11.6198 13.5714 8.07769 13.5714C6.77134 13.5796 5.49019 13.2282 4.38768 12.5592L2.15838 13.2507C2.07016 13.278 1.97574 13.2815 1.88555 13.2609C1.79536 13.2402 1.71291 13.1961 1.64732 13.1335C1.58174 13.0709 1.53556 12.9922 1.5139 12.9061C1.49225 12.82 1.49595 12.7298 1.5246 12.6456Z"
                stroke="#FF600C"
                strokeWidth="1.39286"
                strokeLinejoin="round"
              />
              <path
                d="M5.9781 8.11601C5.9781 8.55115 5.62536 8.9039 5.19022 8.9039C4.75509 8.9039 4.40234 8.55115 4.40234 8.11601C4.40234 7.68087 4.75509 7.32812 5.19022 7.32812C5.62536 7.32812 5.9781 7.68087 5.9781 8.11601Z"
                fill="#FF600C"
                stroke="none"
              />
              <path
                d="M8.90389 8.11601C8.90389 8.55115 8.55114 8.9039 8.11601 8.9039C7.68087 8.9039 7.32812 8.55115 7.32812 8.11601C7.32812 7.68087 7.68087 7.32812 8.11601 7.32812C8.55114 7.32812 8.90389 7.68087 8.90389 8.11601Z"
                fill="#FF600C"
                stroke="none"
              />
              <path
                d="M11.8316 8.11601C11.8316 8.55115 11.4789 8.9039 11.0437 8.9039C10.6086 8.9039 10.2559 8.55115 10.2559 8.11601C10.2559 7.68087 10.6086 7.32812 11.0437 7.32812C11.4789 7.32812 11.8316 7.68087 11.8316 8.11601Z"
                fill="#FF600C"
                stroke="none"
              />
            </>
          ) : (
            <>
              <path
                d="M2.28383 10.3463L2.3929 10.5408L2.32102 10.752L1.50037 13.1623C1.50092 13.1632 1.50198 13.1647 1.50392 13.1666C1.50806 13.1705 1.51567 13.1755 1.52694 13.178C1.53826 13.1806 1.55029 13.1801 1.5609 13.1768C1.56091 13.1768 1.56093 13.1768 1.56095 13.1768L3.96168 12.4322L4.17672 12.3655L4.36919 12.4823C5.47633 13.1541 6.76495 13.5081 8.08053 13.4999L8.08053 13.4998H8.08367C11.6916 13.4998 14.5 10.9698 14.5 8.0051C14.5 5.03445 11.6897 2.5 8.08367 2.5C4.47781 2.5 1.66758 5.03422 1.66733 8.0047C1.66798 8.82044 1.8792 9.62462 2.28383 10.3463ZM1.49985 13.1638L1.49981 13.1639L1.49542 13.1624L1.49541 13.1624L1.02649 13.0028L1.49985 13.1638Z"
                stroke="#FF600C"
              />
              <circle cx="5" cy="8" r="0.75" fill="#FF600C" stroke="none" />
              <circle cx="8" cy="8" r="0.75" fill="#FF600C" stroke="none" />
              <circle cx="11" cy="8" r="0.75" fill="#FF600C" stroke="none" />
            </>
          )}
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <path
                d="M1.5246 12.6456L2.28715 10.4059C1.87303 9.66731 1.65604 8.84263 1.65538 8.00487C1.65538 4.93059 4.53657 2.42871 8.07769 2.42871C11.6188 2.42871 14.5 4.93059 14.5 8.00487C14.5 11.0743 11.6198 13.5714 8.07769 13.5714C6.77134 13.5796 5.49019 13.2282 4.38768 12.5592L2.15838 13.2507C2.07016 13.278 1.97574 13.2815 1.88555 13.2609C1.79536 13.2402 1.71291 13.1961 1.64732 13.1335C1.58174 13.0709 1.53556 12.9922 1.5139 12.9061C1.49225 12.82 1.49595 12.7298 1.5246 12.6456Z"
                stroke="white"
                strokeWidth="1.39286"
                strokeLinejoin="round"
              />
              <path
                d="M5.9781 8.11601C5.9781 8.55115 5.62536 8.9039 5.19022 8.9039C4.75509 8.9039 4.40234 8.55115 4.40234 8.11601C4.40234 7.68087 4.75509 7.32812 5.19022 7.32812C5.62536 7.32812 5.9781 7.68087 5.9781 8.11601Z"
                fill="white"
                stroke="none"
              />
              <path
                d="M8.90389 8.11601C8.90389 8.55115 8.55114 8.9039 8.11601 8.9039C7.68087 8.9039 7.32812 8.55115 7.32812 8.11601C7.32812 7.68087 7.68087 7.32812 8.11601 7.32812C8.55114 7.32812 8.90389 7.68087 8.90389 8.11601Z"
                fill="white"
                stroke="none"
              />
              <path
                d="M11.8316 8.11601C11.8316 8.55115 11.4789 8.9039 11.0437 8.9039C10.6086 8.9039 10.2559 8.55115 10.2559 8.11601C10.2559 7.68087 10.6086 7.32812 11.0437 7.32812C11.4789 7.32812 11.8316 7.68087 11.8316 8.11601Z"
                fill="white"
                stroke="none"
              />
            </>
          ) : (
            <>
              <path
                d="M2.28383 10.3463L2.3929 10.5408L2.32102 10.752L1.50037 13.1623C1.50092 13.1632 1.50198 13.1647 1.50392 13.1666C1.50806 13.1705 1.51567 13.1755 1.52694 13.178C1.53826 13.1806 1.55029 13.1801 1.5609 13.1768C1.56091 13.1768 1.56093 13.1768 1.56095 13.1768L3.96168 12.4322L4.17672 12.3655L4.36919 12.4823C5.47633 13.1541 6.76495 13.5081 8.08053 13.4999L8.08053 13.4998H8.08367C11.6916 13.4998 14.5 10.9698 14.5 8.0051C14.5 5.03445 11.6897 2.5 8.08367 2.5C4.47781 2.5 1.66758 5.03422 1.66733 8.0047C1.66798 8.82044 1.8792 9.62462 2.28383 10.3463ZM1.49985 13.1638L1.49981 13.1639L1.49542 13.1624L1.49541 13.1624L1.02649 13.0028L1.49985 13.1638Z"
                stroke="white"
              />
              <circle cx="5" cy="8" r="0.75" fill="white" stroke="none" />
              <circle cx="8" cy="8" r="0.75" fill="white" stroke="none" />
              <circle cx="11" cy="8" r="0.75" fill="white" stroke="none" />
            </>
          )}
        </SVGIconBase>
      );
    default:
      return null;
  }
};
const FilterIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight = 'normal', ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="9.75" cy="4.75" r="1.75" stroke="#4C5057" strokeWidth="1.5" />
              <path
                d="M2.5 4.75H8M12 4.75H13.5"
                stroke="#4C5057"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="2.5"
                cy="2.5"
                r="1.75"
                transform="matrix(-1 0 0 1 8.75 8.75)"
                stroke="#4C5057"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 11.25H8M4 11.25H2.5"
                stroke="#4C5057"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <path d="M2.5 5H8.5M11.5 5H13.5" stroke="#4C5057" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="5" r="1.5" stroke="#4C5057" />
              <path d="M13.5 11H7.5M4.5 11H2.5" stroke="#4C5057" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="2" cy="2" r="1.5" transform="matrix(-1 0 0 1 8 9)" stroke="#4C5057" />
            </>
          )}
        </SVGIconBase>
      );
    case 'gray400':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="9.75" cy="4.75" r="1.75" stroke="#C7CBD1" strokeWidth="1.5" />
              <path
                d="M2.5 4.75H8M12 4.75H13.5"
                stroke="#C7CBD1"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="2.5"
                cy="2.5"
                r="1.75"
                transform="matrix(-1 0 0 1 8.75 8.75)"
                stroke="#C7CBD1"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 11.25H8M4 11.25H2.5"
                stroke="#C7CBD1"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <path d="M2.5 5H8.5M11.5 5H13.5" stroke="#C7CBD1" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="5" r="1.5" stroke="#C7CBD1" />
              <path d="M13.5 11H7.5M4.5 11H2.5" stroke="#C7CBD1" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="2" cy="2" r="1.5" transform="matrix(-1 0 0 1 8 9)" stroke="#C7CBD1" />
            </>
          )}
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="9.75" cy="4.75" r="1.75" stroke="#FF600C" strokeWidth="1.5" />
              <path
                d="M2.5 4.75H8M12 4.75H13.5"
                stroke="#FF600C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="2.5"
                cy="2.5"
                r="1.75"
                transform="matrix(-1 0 0 1 8.75 8.75)"
                stroke="#FF600C"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 11.25H8M4 11.25H2.5"
                stroke="#FF600C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <path d="M2.5 5H8.5M11.5 5H13.5" stroke="#FF600C" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="5" r="1.5" stroke="#FF600C" />
              <path d="M13.5 11H7.5M4.5 11H2.5" stroke="#FF600C" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="2" cy="2" r="1.5" transform="matrix(-1 0 0 1 8 9)" stroke="#FF600C" />
            </>
          )}
        </SVGIconBase>
      );
    case 'orange400':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="9.75" cy="4.75" r="1.75" stroke="#FFBC99" strokeWidth="1.5" />
              <path
                d="M2.5 4.75H8M12 4.75H13.5"
                stroke="#FFBC99"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="2.5"
                cy="2.5"
                r="1.75"
                transform="matrix(-1 0 0 1 8.75 8.75)"
                stroke="#FFBC99"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 11.25H8M4 11.25H2.5"
                stroke="#FFBC99"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <path d="M2.5 5H8.5M11.5 5H13.5" stroke="#FFBC99" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="5" r="1.5" stroke="#FFBC99" />
              <path d="M13.5 11H7.5M4.5 11H2.5" stroke="#FFBC99" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="2" cy="2" r="1.5" transform="matrix(-1 0 0 1 8 9)" stroke="#FFBC99" />
            </>
          )}
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase {...props}>
          {weight === 'bold' ? (
            <>
              <circle cx="9.75" cy="4.75" r="1.75" stroke="white" strokeWidth="1.5" />
              <path
                d="M2.5 4.75H8M12 4.75H13.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="2.5"
                cy="2.5"
                r="1.75"
                transform="matrix(-1 0 0 1 8.75 8.75)"
                stroke="white"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 11.25H8M4 11.25H2.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <>
              <path d="M2.5 5H8.5M11.5 5H13.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="5" r="1.5" stroke="white" />
              <path d="M13.5 11H7.5M4.5 11H2.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="2" cy="2" r="1.5" transform="matrix(-1 0 0 1 8 9)" stroke="white" />
            </>
          )}
        </SVGIconBase>
      );
    default:
      return null;
  }
};

const MoreIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight, ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase color={color} {...props}>
          {weight === 'bold' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3.5" cy="8" r="1" fill="#4C5057" />
              <circle cx="8" cy="8" r="1" fill="#4C5057" />
              <circle cx="12.5" cy="8" r="1" fill="#4C5057" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="8" r="0.75" fill="#4C5057" />
              <circle cx="8" cy="8" r="0.75" fill="#4C5057" />
              <circle cx="12" cy="8" r="0.75" fill="#4C5057" />
            </svg>
          )}
        </SVGIconBase>
      );
    case 'gray400':
      return (
        <SVGIconBase color={color} {...props}>
          {weight === 'bold' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3.5" cy="8" r="1" fill="#C7CBD1" />
              <circle cx="8" cy="8" r="1" fill="#C7CBD1" />
              <circle cx="12.5" cy="8" r="1" fill="#C7CBD1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="8" r="0.75" fill="#C7CBD1" />
              <circle cx="8" cy="8" r="0.75" fill="#C7CBD1" />
              <circle cx="12" cy="8" r="0.75" fill="#C7CBD1" />
            </svg>
          )}
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase color={color} {...props}>
          {weight === 'bold' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3.5" cy="8" r="1" fill="#FF600C" />
              <circle cx="8" cy="8" r="1" fill="#FF600C" />
              <circle cx="12.5" cy="8" r="1" fill="#FF600C" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="8" r="0.75" fill="#FF600C" />
              <circle cx="8" cy="8" r="0.75" fill="#FF600C" />
              <circle cx="12" cy="8" r="0.75" fill="#FF600C" />
            </svg>
          )}
        </SVGIconBase>
      );
    case 'orange400':
      return (
        <SVGIconBase color={color} {...props}>
          {weight === 'bold' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3.5" cy="8" r="1" fill="#FFBC99" />
              <circle cx="8" cy="8" r="1" fill="#FFBC99" />
              <circle cx="12.5" cy="8" r="1" fill="#FFBC99" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="8" r="0.75" fill="#FFBC99" />
              <circle cx="8" cy="8" r="0.75" fill="#FFBC99" />
              <circle cx="12" cy="8" r="0.75" fill="#FFBC99" />
            </svg>
          )}
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase color={color} {...props}>
          {weight === 'bold' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3.5" cy="8" r="1" fill="white" />
              <circle cx="8" cy="8" r="1" fill="white" />
              <circle cx="12.5" cy="8" r="1" fill="white" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="8" r="0.75" fill="white" />
              <circle cx="8" cy="8" r="0.75" fill="white" />
              <circle cx="12" cy="8" r="0.75" fill="white" />
            </svg>
          )}
        </SVGIconBase>
      );
  }
};

const LinkIconV2: React.FC<SVGIconProps> = ({ color = 'gray700', weight, ...props }) => {
  switch (color) {
    case 'gray700':
      return (
        <SVGIconBase color={color} {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M5.65392 10.2157L8.05663 7.81303C8.61022 7.25944 9.48935 7.24103 10.0202 7.77191C10.5511 8.30279 10.5327 9.18192 9.97912 9.73551L7.53789 12.1767C6.47041 13.2442 4.7735 13.278 3.74773 12.2523C2.72197 11.2265 2.75579 9.52959 3.82327 8.46211L8.46211 3.82327C9.52959 2.75579 11.2265 2.72197 12.2523 3.74773C13.1188 4.61423 13.2292 5.95964 12.5991 7"
              stroke="#4C5057"
              strokeWidth={weight === 'bold' ? 1.5 : 1}
              strokeLinecap="round"
            />
          </svg>
        </SVGIconBase>
      );
    case 'gray400':
      return (
        <SVGIconBase color={color} {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M5.65392 10.2157L8.05663 7.81303C8.61022 7.25944 9.48935 7.24103 10.0202 7.77191C10.5511 8.30279 10.5327 9.18192 9.97912 9.73551L7.53789 12.1767C6.47041 13.2442 4.7735 13.278 3.74773 12.2523C2.72197 11.2265 2.75579 9.52959 3.82327 8.46211L8.46211 3.82327C9.52959 2.75579 11.2265 2.72197 12.2523 3.74773C13.1188 4.61423 13.2292 5.95964 12.5991 7"
              stroke="#C7CBD1"
              strokeWidth={weight === 'bold' ? 1.5 : 1}
              strokeLinecap="round"
            />
          </svg>
        </SVGIconBase>
      );
    case 'orange800':
      return (
        <SVGIconBase color={color} {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M5.65392 10.2157L8.05663 7.81303C8.61022 7.25944 9.48935 7.24103 10.0202 7.77191C10.5511 8.30279 10.5327 9.18192 9.97912 9.73551L7.53789 12.1767C6.47041 13.2442 4.7735 13.278 3.74773 12.2523C2.72197 11.2265 2.75579 9.52959 3.82327 8.46211L8.46211 3.82327C9.52959 2.75579 11.2265 2.72197 12.2523 3.74773C13.1188 4.61423 13.2292 5.95964 12.5991 7"
              stroke="#FF600C"
              strokeWidth={weight === 'bold' ? 1.5 : 1}
              strokeLinecap="round"
            />
          </svg>
        </SVGIconBase>
      );
    case 'white':
      return (
        <SVGIconBase color={color} {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M5.65392 10.2157L8.05663 7.81303C8.61022 7.25944 9.48935 7.24103 10.0202 7.77191C10.5511 8.30279 10.5327 9.18192 9.97912 9.73551L7.53789 12.1767C6.47041 13.2442 4.7735 13.278 3.74773 12.2523C2.72197 11.2265 2.75579 9.52959 3.82327 8.46211L8.46211 3.82327C9.52959 2.75579 11.2265 2.72197 12.2523 3.74773C13.1188 4.61423 13.2292 5.95964 12.5991 7"
              stroke="white"
              strokeWidth={weight === 'bold' ? 1.5 : 1}
              strokeLinecap="round"
            />
          </svg>
        </SVGIconBase>
      );
    default:
      return null;
  }
};

const TooltipIcon: React.FC<SVGIconProps> = ({ color = 'gray700', weight, ...props }) => {
  const getColor = () => {
    switch (color) {
      case 'gray700':
        return '#4C5057';
      case 'gray400':
        return '#C7CBD1';
      case 'orange800':
        return '#FF600C';
      case 'white':
        return '#FFFFFF';
      default:
        return '#4C5057';
    }
  };
  return (
    <SVGIconBase color={color} {...props}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6.25" stroke={getColor()} strokeWidth={weight === 'bold' ? 1.5 : 1} />
        <path
          d="M5.98883 5.4618C6.29634 4.79648 7.05958 4.3999 8.03799 4.3999C8.68439 4.3999 9.23681 4.58228 9.62915 4.90569C10.0226 5.23004 10.2496 5.69203 10.2496 6.23762C10.2496 6.60506 10.1574 6.91858 9.98044 7.18934C9.80402 7.4593 9.54638 7.6822 9.2224 7.87373L9.22116 7.87445C8.91952 8.04817 8.74244 8.18422 8.63518 8.32216C8.53097 8.45617 8.48761 8.59856 8.46974 8.79789L8.4686 8.80733C8.44367 8.9687 8.38772 9.11048 8.28617 9.21265C8.18209 9.31738 8.03918 9.37139 7.86141 9.37139C7.66164 9.37139 7.49929 9.30004 7.38811 9.1752C7.27835 9.05196 7.22508 8.88422 7.22508 8.70059V8.61295C7.22508 8.31194 7.3013 8.05756 7.45662 7.83343C7.61075 7.61102 7.83921 7.42355 8.13538 7.24941L8.13656 7.2487C8.42135 7.07766 8.60199 6.935 8.71242 6.78692C8.82025 6.64233 8.86543 6.48667 8.86543 6.27913C8.86543 6.05035 8.7761 5.86023 8.62508 5.7267C8.4735 5.59268 8.25413 5.51089 7.98688 5.51089C7.74418 5.51089 7.54868 5.57038 7.39759 5.67912C7.24641 5.78793 7.1326 5.95114 7.0626 6.17044L7.06129 6.17423C7.00657 6.32683 6.92997 6.44322 6.82447 6.52089C6.71788 6.59936 6.59081 6.63168 6.44876 6.63168C6.2696 6.63168 6.11731 6.57709 6.0099 6.47048C5.90279 6.36417 5.84961 6.21537 5.84961 6.0439C5.84961 5.83367 5.89411 5.65126 5.987 5.46576L5.98883 5.4618Z"
          fill={getColor()}
        />
        <path
          d="M7.10352 10.8722C7.10352 10.4531 7.4395 10.1245 7.8558 10.1245C8.27277 10.1245 8.60352 10.4538 8.60352 10.8722C8.60352 11.2898 8.27347 11.6245 7.8558 11.6245C7.4388 11.6245 7.10352 11.2905 7.10352 10.8722Z"
          fill={getColor()}
        />
      </svg>
    </SVGIconBase>
  );
};
const SVGIcon = {
  Link: LinkIcon,
  Arrow: ArrowIcon,
  TopRightFillArrow: TopRightFillArrowIcon,
  TopFillArrow: TopFillArrowIcon,
  Refresh: RefreshIcon,
  Minus: MinusIcon,
  Bell: BellIcon,
  Close: CloseIcon,
  Check: CheckIcon,
  Calendar: CalendarIcon,
  Note: NoteIcon,
  Home: HomeIcon,
  BellAlert: BellAlertIcon,
  Talk: TalkIcon,
  Plus: PlusIcon,
  Search: SearchIcon,
  Leader: LeaderIcon,
  Profile: ProfileIcon,
  More: MoreIcon,
  LinkV2: LinkIconV2,
  Filter: FilterIcon,
  Bullet: BulletIcon,
  Tooltip: TooltipIcon,
};

export default SVGIcon;
